import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { ChevronDown, ChevronLeft, Plus } from 'lucide-react'

import { ClientNotFound } from '@/entities/client/ui/client-not-found'
import { EditPetDialog } from '@/features/pet-management/ui/EditPetDialog'
import { useClientProfile } from '@/hooks/use-client-profile'
import { PetCard } from '@/pages/external-client-page/ui/pet-card'
import { useAuth } from '@/shared/auth'
import { cn } from '@/shared/lib/styles/class-merge.ts'
import { Badge } from '@/shared/ui/badge.tsx'
import { Button } from '@/shared/ui/button.tsx'
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card.tsx'
import { LanguageSwitchButton } from '@/shared/ui/language-switch-button'
import { Loader } from '@/shared/ui/loader'
import { Pet, Reservation } from '@monorepo/functions/src/types/reservations'

import { ExternalClientReservationCard } from './ui/external-client-reservation-card'
import { FloatingActionButton } from './ui/floating-action-button'
import { GeneralInformation } from './ui/general-information'

export default function ExternalClientPage() {
    const { user } = useAuth()
    console.log('user', user)
    const { clientProfile, isLoading, updateClient } = useClientProfile(user?.uid)
    const [clientReservations, setClientReservations] = useState<Reservation[]>([])
    const [showAllReservations, setShowAllReservations] = useState(false)
    const [isAddPetDialogOpen, setIsAddPetDialogOpen] = useState(false)
    const [newPet, setNewPet] = useState<Pet>({
        name: '',
        breed: '',
        size: 'pequeño',
        weight: 0,
        sex: 'M',
        isNeutered: false,
        age: '',
        personality: '',
    })

    const { t } = useTranslation(undefined, { keyPrefix: 'externalClient' })

    useEffect(() => {
        if (clientProfile) {
            console.log('clientProfile', clientProfile)
            setClientReservations(clientProfile.reservations)
        }
    }, [clientProfile])

    const handlePetSave = async (updatedPet: Pet) => {
        try {
            if (!clientProfile) return

            // Find if the pet already exists (by id if available, otherwise by name)
            const petIndex = clientProfile.pets.findIndex(
                p => (updatedPet.id && p.id === updatedPet.id) || (!updatedPet.id && p.name === updatedPet.name),
            )

            let updatedPets: Pet[]

            if (petIndex >= 0) {
                // Update existing pet
                updatedPets = [...clientProfile.pets]
                updatedPets[petIndex] = updatedPet
            } else {
                // Add new pet
                updatedPets = [...clientProfile.pets, updatedPet]
            }

            // Update the client in the backend
            await updateClient({
                pets: updatedPets,
            })
        } catch (error) {
            console.error('Error updating pet:', error)
        }
    }

    const handlePetDelete = async (petToDelete: Pet) => {
        try {
            if (!clientProfile) return

            // Filter out the pet to delete (by id if available, otherwise by name)
            const updatedPets = clientProfile.pets.filter(
                p => (petToDelete.id && p.id !== petToDelete.id) || (!petToDelete.id && p.name !== petToDelete.name),
            )

            // Update the client in the backend
            await updateClient({
                pets: updatedPets,
            })
        } catch (error) {
            console.error('Error deleting pet:', error)
        }
    }

    const handleAddNewPet = () => {
        // Reset the new pet form with default values and a unique ID
        setNewPet({
            id: `new-pet-${Date.now()}`, // Generate a temporary unique ID
            name: '',
            breed: '',
            size: 'pequeño',
            weight: 0,
            sex: 'M',
            isNeutered: false,
            age: '',
            personality: '',
        })
        setIsAddPetDialogOpen(true)
    }

    const handleNewPetSave = (pet: Pet) => {
        handlePetSave(pet)
        setIsAddPetDialogOpen(false)
    }

    const displayedReservations = showAllReservations ? clientReservations : clientReservations.slice(0, 4)

    if (isLoading) {
        return <Loader />
    }

    // Only show ClientNotFound if we've finished loading and there's no client
    // This prevents showing the banner before the client data is actually loaded
    if (!isLoading && !clientProfile) {
        return <ClientNotFound />
    }

    return (
        <div className='container mx-auto max-w-3xl px-4 py-4 pb-24'>
            <>
                <FloatingActionButton userId={clientProfile.id} />
                <div className='mb-6 flex items-center justify-between'>
                    <div className='flex items-center gap-2'>
                        <Link to='/'>
                            <Button variant='ghost' size='icon'>
                                <ChevronLeft className='h-4 w-4' />
                            </Button>
                        </Link>
                        <h1 className='text-2xl font-bold'>{t('clientProfile', 'My Profile')}</h1>
                    </div>
                    <div className='flex items-center gap-2'>
                        <LanguageSwitchButton />
                    </div>
                </div>

                <div className='grid gap-4'>
                    <GeneralInformation clientProfile={clientProfile} onSave={updateClient} />

                    <Card>
                        <CardHeader className='border-b'>
                            <div className='flex items-center justify-between'>
                                <div className='flex items-center gap-2'>
                                    <CardTitle>{t('reservations', 'Reservations')}</CardTitle>
                                    <Badge variant='outline' className='ml-2'>
                                        {clientReservations.length}
                                    </Badge>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className='p-0'>
                            <div className='divide-y'>
                                {displayedReservations.map(reservation => (
                                    <ExternalClientReservationCard
                                        key={reservation.id}
                                        reservation={reservation}
                                        setClientReservations={setClientReservations}
                                    />
                                ))}
                            </div>
                            {clientReservations.length > 4 && (
                                <div
                                    className='flex cursor-pointer items-center justify-center border-t p-2 transition-colors hover:bg-accent/50'
                                    onClick={e => {
                                        e.stopPropagation()
                                        setShowAllReservations(!showAllReservations)
                                    }}
                                >
                                    <div className='flex items-center gap-2 text-sm text-muted-foreground'>
                                        {showAllReservations ? (
                                            <span>{t('showLess', 'Show less')}</span>
                                        ) : (
                                            <span>
                                                {t('showMore', 'Show')} {clientReservations.length - 4}{' '}
                                                {t('more', 'more')}
                                            </span>
                                        )}
                                        <ChevronDown
                                            className={cn(
                                                'h-4 w-4 transition-transform',
                                                showAllReservations ? 'rotate-180' : '',
                                            )}
                                        />
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle>{t('pets', 'Pets')}</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className='grid gap-4'>
                                {clientProfile.pets.map((pet, index) => (
                                    <PetCard
                                        key={pet.id ?? `pet-${index}-${pet.name}`}
                                        pet={pet}
                                        onSave={handlePetSave}
                                        onDelete={handlePetDelete}
                                    />
                                ))}
                            </div>
                            <Button className='mt-4 w-full' onClick={handleAddNewPet}>
                                <Plus className='mr-2 h-4 w-4' />
                                {t('addNewPet', 'Add New Pet')}
                            </Button>
                        </CardContent>
                    </Card>
                </div>

                {/* Add New Pet Dialog */}
                <EditPetDialog
                    pet={newPet}
                    isOpen={isAddPetDialogOpen}
                    onClose={() => setIsAddPetDialogOpen(false)}
                    onSave={handleNewPetSave}
                    hideObservations={true}
                />
            </>
        </div>
    )
}
